/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from "zod";

import { useVenuesDataStore } from "@/store/useVenuesDataStore";

export function getFormConfig(translations: (arg0: string) => any) {
  const t = (key: string) => translations(key);

  const formSchema = z
    .object({
      email: z
        .string({ required_error: t("receiptPage.email.required") })
        .trim()
        .email({ message: t("receiptPage.email.invalid") }),
      show_expense_fields: z.boolean().default(false),

      number_of_guests: z.string().optional(),
      attendees_names: z.string().optional(),
      business_purpose: z.string().optional(),
      company: z.string().optional(),

      address: z.string().optional(),
      city_and_state: z.string().optional(),
      zip_code: z.string().optional(),
      country: z.string().optional(),
    })
    .refine((data) => {
      if (!data.show_expense_fields) return true;

      return (
        !!data.number_of_guests &&
        !!data.attendees_names &&
        !!data.business_purpose &&
        !!data.company &&
        !!data.address &&
        !!data.city_and_state &&
        !!data.zip_code &&
        !!data.country
      );
    });

  const getReceiptFormConfig = {
    id: "login-form",
    validation: formSchema,
    fields: [
      {
        name: "email",
        label: t("receiptPage.email.label"),
        placeholder: t("receiptPage.email.placeholder"),
        type: "text",
        validation: formSchema._def.schema.shape.email,
      },
      {
        name: "show_expense_fields",
        label: t("receiptPage.showExpenseFields.label"),
        type: "checkbox",
        validation: formSchema._def.schema.shape.show_expense_fields,
      },
      {
        name: "all_fields_optional",
        label: t("receiptPage.allFieldsOptional"),
        type: "label",
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "number_of_guests",
        label: t("receiptPage.numberOfGuests.label"),
        placeholder: " ",
        type: "text",
        validation: formSchema._def.schema.shape.number_of_guests,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "attendees_names",
        label: t("receiptPage.attendeesNames.label"),
        placeholder: " ",
        type: "text-area",
        validation: formSchema._def.schema.shape.attendees_names,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "business_purpose",
        label: t("receiptPage.businessPurpose.label"),
        placeholder: " ",
        type: "text",
        validation: formSchema._def.schema.shape.business_purpose,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "company",
        label: t("receiptPage.company.label"),
        placeholder: " ",
        type: "text",
        validation: formSchema._def.schema.shape.company,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "all_fields_optional",
        label: t("receiptPage.companyAddress"),
        type: "label",
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "address",
        label: t("receiptPage.address.label"),
        placeholder: " ",
        type: "text",
        validation: formSchema._def.schema.shape.address,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
      {
        name: "city_zip_group",
        type: "input-group",
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
        inputs: [
          {
            name: "city_and_state",
            label: t("receiptPage.cityAndState.label"),
            placeholder: " ",
            type: "text",
            validation: formSchema._def.schema.shape.city_and_state,
            flexGrow: 2,
            hidden: (formData: Record<string, any>) =>
              !formData.show_expense_fields,
          },
          {
            name: "zip_code",
            label: t("receiptPage.zipCode.label"),
            placeholder: " ",
            type: "text",
            validation: formSchema._def.schema.shape.zip_code,
            flexGrow: 1,
            hidden: (formData: Record<string, any>) =>
              !formData.show_expense_fields,
          },
        ],
      },
      {
        name: "country",
        label: t("receiptPage.country.label"),
        placeholder: " ",
        type: "text",
        validation: formSchema._def.schema.shape.country,
        hidden: (formData: Record<string, any>) =>
          !formData.show_expense_fields,
      },
    ],
    onSubmit: (data: Record<string, any>) => {
      useVenuesDataStore.getState().setVenuesData({ email: data.email });
    },
  };

  return getReceiptFormConfig;
}
